import { brand } from '@shared/brand'
import { cn } from '../lib/cn'

export interface LogoProps {
  /** 'mark': Kachel · 'bare': Zeichen ohne Kachel (KI-Assistent, ab 14 px) · 'lockup': Kachel + Wortmarke */
  variant?: 'mark' | 'bare' | 'lockup'
  /** Kantenlänge der Bildmarke (Kachel) in px; Mindestgröße 16 (mit Kachel) bzw. 14 (ohne) */
  size?: number
  /** Einheit in derselben Farbe wie der Rahmen statt orange (Stempel, Belegdruck schwarz-weiß) */
  mono?: boolean
  /** zugänglicher Name; `null` = rein dekorativ */
  title?: string | null
  className?: string
}

// Geometrie der Marke „Ordaneo“ (ordaneo-website/assets/brand/BRAND.md §3–§5), Bildmarke A „Eckstein“: Raster 32 × 32,
// eckiges O als offener Rahmen, die Einheit schließt die letzte Ecke. Dieselben Maße stehen in web/public/ordaneo-mark.svg.
const TILE = 32
/** Rahmen (O) mit Kachel: Strich 3, runde Enden; offen unten rechts, wo die Einheit sitzt */
const FRAME = 'M14.5 23.5H13a4.5 4.5 0 0 1-4.5-4.5v-6A4.5 4.5 0 0 1 13 8.5h6a4.5 4.5 0 0 1 4.5 4.5v1.5'
/** Rahmen ohne Kachel: dieselbe Form um die Mitte auf das 1,5-Fache vergrößert, Strich 4,5 */
const FRAME_BARE = 'M13.75 27.25H11.5a6.75 6.75 0 0 1-6.75-6.75v-9A6.75 6.75 0 0 1 11.5 4.75h9a6.75 6.75 0 0 1 6.75 6.75v2.25'
/** Wortmarke „ordaneo“ als Linienzüge (x-Höhe 100, Strich 21, Querstrich des e 18), Ursprung = Oberkante x-Höhe */
const WORDMARK = {
  width: 721.92,
  height: 142,
  /** Abstand Oberkante Datei → Oberkante x-Höhe (Oberlänge des d) */
  ascender: 40,
  xHeight: 100,
  bar: 'M607.13 50H514.42',
  strokes:
    'M10.5 50a41.5 41.5 0 1 0 83 0a41.5 41.5 0 1 0 -83 0' +
    'M128.5 0V100' +
    'M128.5 20.74A40 40 0 0 1 176.08 13.18' +
    'M264.75 24.03A41.5 41.5 0 1 0 264.75 75.97' +
    'M264.75 -40V100' +
    'M373.62 24.03A41.5 41.5 0 1 0 373.62 75.97' +
    'M373.62 0V100' +
    'M410.62 0V100' +
    'M410.62 20.74A40 40 0 0 1 479.42 48.5V100' +
    'M596.79 57.18A41.5 41.5 0 1 0 586.76 77.77' +
    'M628.42 50a41.5 41.5 0 1 0 83 0a41.5 41.5 0 1 0 -83 0',
} as const

function WordmarkPaths() {
  return (
    <g fill="none" stroke="currentColor" strokeLinecap="butt" strokeLinejoin="miter">
      <path strokeWidth="18" d={WORDMARK.bar} />
      <path strokeWidth="21" d={WORDMARK.strokes} />
    </g>
  )
}

/**
 * Inhalt der Kachel-Bildmarke (32 × 32). Die Einheit ist ohne Hausfarbe orange (--color-logo-unit, Entscheidung
 * Christoph 20.10.2026: #e8650e auf der Graphit-Kachel, im dunklen Theme #c9530a auf der hellen Kachel) und übernimmt
 * sonst die Hausfarbe des Mandanten.
 */
function MarkShapes({ mono }: { mono?: boolean }) {
  return (
    <>
      <rect width={TILE} height={TILE} rx="8" fill="var(--color-inverse)" />
      <path d={FRAME} fill="none" stroke="var(--color-fg-inverse)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="18" y="18" width="7" height="7" rx="1.75" fill={mono ? 'var(--color-fg-inverse)' : 'var(--color-logo-unit)'} />
    </>
  )
}

/**
 * Bildmarke nach design-system §1.3 bzw. BRAND.md §3. Die „Einheit“ ist orange oder trägt die Hausfarbe des Mandanten;
 * ohne Kachel (`bare`) gilt --color-logo-unit-bare (Orange passend zum Grund bzw. der Akzent des Mandanten).
 */
export function Logo({ variant = 'mark', size = 32, mono, title = brand.name, className }: LogoProps) {
  const a11y = title === null ? ({ 'aria-hidden': true } as const) : ({ role: 'img', 'aria-label': title } as const)
  if (variant === 'bare') {
    return (
      <svg viewBox="0 0 32 32" width={size} height={size} fill="none" className={cn('shrink-0', className)} {...a11y}>
        <path d={FRAME_BARE} stroke="currentColor" strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round" />
        <rect x="19" y="19" width="10.5" height="10.5" rx="2.625" fill={mono ? 'currentColor' : 'var(--color-logo-unit-bare)'} />
      </svg>
    )
  }
  if (variant === 'mark') {
    return (
      <svg viewBox="0 0 32 32" width={size} height={size} fill="none" className={cn('shrink-0', className)} {...a11y}>
        <MarkShapes mono={mono} />
      </svg>
    )
  }
  // Lockup (BRAND.md §5): Kachel = 1,5 × x-Höhe, mittig zur x-Höhe; Abstand Kachel–Wortmarke = 0,35 × Kachelhöhe.
  // Einheiten der Wortmarke: Kachel 150, Abstand 52,5; die Oberlänge des d ragt 15 über die Kachel hinaus.
  const tile = 1.5 * WORDMARK.xHeight
  const gap = 0.35 * tile
  const top = WORDMARK.ascender - (tile - WORDMARK.xHeight) / 2
  const width = tile + gap + WORDMARK.width
  const height = top + tile
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width={(size * width) / tile}
      height={(size * height) / tile}
      fill="none"
      className={cn('shrink-0 text-fg', className)}
      {...a11y}
    >
      <g transform={`translate(0 ${top}) scale(${tile / TILE})`}>
        <MarkShapes mono={mono} />
      </g>
      <g transform={`translate(${tile + gap} ${WORDMARK.ascender})`}>
        <WordmarkPaths />
      </g>
    </svg>
  )
}

export interface WordmarkProps {
  /** Höhe der Kleinbuchstaben (x-Höhe) in px; Mindestgröße der ganzen Wortmarke 14 px (x-Höhe ≈ 10) */
  xHeight?: number
  /** zugänglicher Name; `null` = rein dekorativ */
  title?: string | null
  className?: string
}

/** Wortmarke „ordaneo“ als Pfade (keine Schrift, nie mit Inter nachsetzen); Farbe über `color` (currentColor). */
export function Wordmark({ xHeight = 16, title = brand.name, className }: WordmarkProps) {
  const a11y = title === null ? ({ 'aria-hidden': true } as const) : ({ role: 'img', 'aria-label': title } as const)
  const scale = xHeight / WORDMARK.xHeight
  return (
    <svg
      viewBox={`0 0 ${WORDMARK.width} ${WORDMARK.height}`}
      width={WORDMARK.width * scale}
      height={WORDMARK.height * scale}
      fill="none"
      className={cn('shrink-0', className)}
      {...a11y}
    >
      <g transform={`translate(0 ${WORDMARK.ascender})`}>
        <WordmarkPaths />
      </g>
    </svg>
  )
}
